import { useCallback, useEffect, useState } from "react";
import { adminCall } from "@/lib/adminApi";
import { COUNTRIES } from "@/lib/countries";
import { EXPERIENCE, EXPERTISE, TRACKS } from "@/pages/Careers";
import { inputClass } from "@/components/forms/Fields";
import RecordDetail from "./RecordDetail";

export const LEAD_STATUS = ["new", "reviewing", "contacted", "closed"];
export const TALENT_STATUS = ["new", "reviewing", "shortlisted", "declined"];
export const label = (pairs, v) => (pairs.find(([k]) => k === v) || [v, v])[1];
const date = (iso) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
const sel = `${inputClass} !w-auto !py-2 text-[13px]`;

const Select = ({ value, onChange, options, all, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={sel} data-testid={testId}>
    <option value="">{all}</option>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function Filters({ tab, f, set }) {
  const st = (tab === "leads" ? LEAD_STATUS : TALENT_STATUS).map((s) => [s, s]);
  return (
    <div className="flex flex-wrap gap-2">
      <Select value={f.status || ""} onChange={(v) => set({ status: v })} options={st} all="All statuses" testId="admin-filter-status" />
      {tab === "talent" && (
        <>
          <Select value={f.track || ""} onChange={(v) => set({ track: v })} options={TRACKS} all="All tracks" testId="admin-filter-track" />
          <Select value={f.expertise || ""} onChange={(v) => set({ expertise: v })} options={EXPERTISE} all="All expertise" testId="admin-filter-expertise" />
          <Select value={f.experience || ""} onChange={(v) => set({ experience: v })} options={EXPERIENCE} all="All experience" testId="admin-filter-experience" />
          <Select value={f.country || ""} onChange={(v) => set({ country: v })} options={COUNTRIES.map((c) => [c.code, c.name])} all="All countries" testId="admin-filter-country" />
        </>
      )}
    </div>
  );
}

const Row = ({ tab, r, onOpen }) => (
  <tr onClick={() => onOpen(r.id)} data-testid={`admin-row-${r.id}`} className="cursor-pointer border-t border-fg/10 hover:bg-fg/[0.03]">
    <td className="py-3 pr-4">{r.unread && <span aria-label="Unread" className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-fg" />}{date(r.created_at)}</td>
    <td className="py-3 pr-4 text-fg"><button type="button" className="fa-link rounded-sm text-left">{r.contact.full_name}</button></td>
    {tab === "leads" ? (
      <>
        <td className="py-3 pr-4">{r.contact.organization}</td>
        <td className="py-3 pr-4">{r.contact.country}</td>
        <td className="py-3 pr-4">{r.callback.requested ? "Callback" : "—"}</td>
        <td className="py-3 pr-4">{r.follow_up?.submitted_at ? "Sent" : "Incomplete"}</td>
      </>
    ) : (
      <>
        <td className="py-3 pr-4">{label(TRACKS, r.track)} applicant</td>
        <td className="py-3 pr-4">{r.country_of_residence}</td>
        <td className="py-3 pr-4">{label(EXPERIENCE, r.experience_band)}</td>
        <td className="py-3 pr-4">{r.expertise.map((e) => label(EXPERTISE, e)).join(", ")}</td>
      </>
    )}
    <td className="py-3 capitalize">{r.status}</td>
  </tr>
);

export default function AdminQueue({ onSignedOut }) {
  const [tab, setTab] = useState("leads");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries({ ...filters, page }).filter(([, v]) => v));
    const [list, sum] = await Promise.all([adminCall("get", `/${tab}`, null, params), adminCall("get", "/summary")]);
    if (list.status === 401) return onSignedOut();
    if (!list.ok) return setError(list.message);
    setError("");
    setData(list.data);
    if (sum.ok) setSummary(sum.data);
  }, [tab, filters, page, onSignedOut]);

  useEffect(() => { load(); }, [load]);

  const switchTab = (t) => { if (t === tab) return; setData(null); setTab(t); setFilters({}); setPage(1); setOpen(null); };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-6">
      <p className="text-[12px] text-subtle" data-testid="admin-email-status">{summary?.email_delivery === "configured" ? "Email alerts are configured. New submissions also appear here as unread." : "Email notifications are not connected. New submissions appear here as unread."}{summary?.email_needs_review > 0 && ` ${summary.email_needs_review} email alert(s) failed or need review.`}</p>
      <div role="tablist" className="flex gap-6 border-b border-fg/10">
        {["leads", "talent"].map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => switchTab(t)} data-testid={`admin-tab-${t}`}
            className={`fa-link -mb-px border-b pb-3 text-[14px] ${tab === t ? "border-fg text-fg" : "border-transparent text-subtle hover:text-fg"}`}>
            {t === "leads" ? "Leads" : "Talent"}
            {summary?.unread[t] > 0 && <span className="ml-2 rounded-full bg-fg px-1.5 text-[11px] text-canvas" data-testid={`admin-unread-${t}`}>{summary.unread[t]}</span>}
          </button>
        ))}
      </div>
      <Filters tab={tab} f={filters} set={(p) => { setFilters((f) => ({ ...f, ...p })); setPage(1); }} />
      {error && <p role="alert" className="text-[14px] text-danger">{error}</p>}
      {open ? (
        <RecordDetail tab={tab} id={open} onBack={() => { setOpen(null); load(); }} onSignedOut={onSignedOut} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-[13px] text-subtle" data-testid="admin-table">
            <tbody>{data?.items.map((r) => <Row key={r.id} tab={tab} r={r} onOpen={setOpen} />)}</tbody>
          </table>
          {data && !data.items.length && <p className="py-8 text-subtle">No records.</p>}
          <div className="mt-4 flex items-center gap-4 text-[13px] text-subtle">
            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="fa-link rounded-sm disabled:opacity-40">Previous</button>
            <span>Page {page} of {pages} · {data?.total ?? 0} total</span>
            <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className="fa-link rounded-sm disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
